// ============================================================
// UrbanWear — Cliente HTTP (Axios)
// - Inyecta JWT automáticamente
// - NO sobreescribe Content-Type en uploads multipart
// - Maneja 401 → logout automático
// ============================================================

import axios from 'axios'
import { useAuthStore } from '@/store'

const BASE_URL = import.meta.env.VITE_API_URL ?? ''

export const api = axios.create({
  baseURL: `${BASE_URL}/api`,
  // ⚠️ NO poner Content-Type aquí — Axios lo establece automáticamente
  // según el tipo de body (JSON o FormData)
  timeout: 15_000,
})

// ---- Interceptor: inyectar JWT pero respetar Content-Type del request ----
api.interceptors.request.use(config => {
  const token = useAuthStore.getState().token
  if (token) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Solo establecer JSON si no es FormData (upload de archivos)
  if (!(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json'
  }
  // Si es FormData, dejar que Axios calcule el boundary automáticamente
  return config
})

// ---- Interceptor de respuesta: manejo global de errores ----
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      useAuthStore.getState().logout()
      window.location.href = '/login'
    }
    return Promise.reject(err)
  }
)

// ============================================================
// Tipos de respuesta
// ============================================================

export interface ProductList {
  total:    number
  page:     number
  pageSize: number
  pages:    number
  items:    ProductSummary[]
}

export interface ProductSummary {
  id:              string
  sku:             string
  name:            string
  slug:            string
  gender:          string
  price:           number
  comparePrice?:   number
  priceWholesale?: number
  priceReseller?:  number
  priceAAA?:       number
  categoryName:    string
  brandName:       string
  primaryImage?:   string
  isNew:           boolean
  onSale:          boolean
  avgRating:       number
  reviewCount:     number
  totalStock:      number
  isActive:        boolean
}

export interface ProductDetail extends ProductSummary {
  description?:   string
  seoTitle:       string
  seoDescription: string
  tags:           string[]
  images:         { url: string; altText?: string; isPrimary: boolean }[]
  variantGroups:  VariantGroup[]
  reviews:        Review[]
}

export interface VariantGroup {
  color:     string
  colorHex?: string
  sizes:     VariantSize[]
}

export interface VariantSize {
  variantId: string
  sizeName:  string
  available: number
  inStock:   boolean
}

export interface Review {
  author:    string
  rating:    number
  title?:    string
  body?:     string
  createdAt: string
}

export interface Category {
  id:        number
  name:      string
  slug:      string
  parentId?: number
  gender?:   string
}

export interface CartResponse {
  id:        string
  items:     CartItemResponse[]
  subtotal:  number
  itemCount: number
}

export interface CartItemResponse {
  id:             string
  variantId:      string
  productName:    string
  variantInfo:    string
  imageUrl?:      string
  unitPrice:      number
  totalPrice:     number
  quantity:       number
  availableStock: number
}

export interface AuthResponse {
  token:     string
  tokenType: string
  expiresIn: number
  user: {
    id:          string
    email:       string
    fullName:    string
    role:        string
    isWholesale: boolean
  }
}

export interface Order {
  id:          string
  orderNumber: string
  status:      string
  total:       number
  orderType:   string
  createdAt:   string
  itemCount:   number
}

export interface CreateProductPayload {
  sku:             string
  name:            string
  description:     string
  categoryId:      number
  brandId:         number
  gender:          string
  price:           number
  priceWholesale?: number
  priceReseller?:  number
  priceAAA?:       number
  comparePrice?:   number
  tags:            string[]
  imageUrl?:       string
  imageAlt?:       string
  sizes?:          string
  color?:          string
  colorHex?:       string
  initialStock?:   number
}

export interface InventoryAdjustPayload {
  variantId:    string
  warehouseId:  number
  quantity:     number
  reason: 		string
}

// ============================================================
// Funciones de la API
// ============================================================

// Catálogo
export const fetchProducts  = (params?: Record<string, unknown>) =>
  api.get<ProductList>('/products', { params }).then(r => r.data)

export const fetchProduct   = (slug: string) =>
  api.get<ProductDetail>(`/products/${slug}`).then(r => r.data)

export const fetchFeatured  = () =>
  api.get<ProductSummary[]>('/products/featured').then(r => r.data)

export const fetchCategories = () =>
  api.get<Category[]>('/categories').then(r => r.data)

export const fetchBrands    = () =>
  api.get<{ id: number; name: string; slug: string }[]>('/brands').then(r => r.data)

// Auth
export const login = (email: string, password: string) =>
  api.post<AuthResponse>('/auth/login', { email, password }).then(r => r.data)

export const register = (data: {
  firstName: string; lastName: string
  email: string; password: string; phone?: string; role?: string
}) => api.post<AuthResponse>('/auth/register', data).then(r => r.data)

// Carrito
export const fetchCart      = () =>
  api.get<CartResponse>('/cart').then(r => r.data)

export const addToCart      = (variantId: string, quantity: number) =>
  api.post('/cart/items', { variantId, quantity }).then(r => r.data)

export const updateCartItem = (itemId: string, quantity: number) =>
  api.put(`/cart/items/${itemId}`, { quantity }).then(r => r.data)

export const removeCartItem = (itemId: string) =>
  api.delete(`/cart/items/${itemId}`).then(r => r.data)

// Pedidos
export const fetchMyOrders = () =>
  api.get<Order[]>('/orders').then(r => r.data)

export const createOrder = (data: {
  cartId: string; shippingName: string; shippingStreet: string
  shippingCity: string; shippingState: string; shippingZip: string
  customerNote?: string
}) => api.post<{ id: string; orderNumber: string; total: number }>('/orders', data).then(r => r.data)

// Admin — pedidos
export const fetchAdminOrders  = (page = 1, status?: string) =>
  api.get('/admin/orders', { params: { page, status } }).then(r => r.data)

export const fetchOrderDetail  = (id: string) =>
  api.get(`/admin/orders/${id}`).then(r => r.data)

export const updateOrderStatus = (id: string, status: string) =>
  api.put(`/admin/orders/${id}/status`, null, { params: { status } }).then(r => r.data)

// Admin — inventario
export const fetchInventory = (warehouseId?: number) =>
  api.get('/inventory', { params: { warehouseId } }).then(r => r.data)

export const fetchLowStock  = () =>
  api.get('/inventory/low-stock').then(r => r.data)

export const adjustInventory = (data: InventoryAdjustPayload) =>
  api.post('/inventory/adjust', data).then(r => r.data)

export const fetchWarehouses = () =>
  api.get<{ id: number; name: string; type: string; city: string }[]>('/warehouses').then(r => r.data)

// Admin — productos
export const createProduct = (data: CreateProductPayload) =>
  api.post('/products', data).then(r => r.data)

// ============================================================
// Upload de imagen — usa FormData, NO JSON
// El interceptor detecta FormData y NO agrega Content-Type
// para que Axios calcule el boundary multipart automáticamente
// ============================================================
export const uploadImage = async (file: File): Promise<string> => {
  const fd = new FormData()
  fd.append('file', file)

  // Axios detecta FormData y pone el Content-Type correcto con boundary
  const res = await api.post<{ url: string }>('/upload', fd)
  return res.data.url
}
export const updateProduct = (id: string, data: CreateProductPayload) =>
  api.put(`/products/${id}`, data).then(r => r.data)

// Galería de imágenes del producto (la primera es la principal)
export interface GalleryImage { url: string; altText?: string | null }
export const fetchProductImages = (id: string): Promise<GalleryImage[]> =>
  api.get(`/products/${id}/images`).then(r => r.data)
export const saveProductImages = (id: string, images: GalleryImage[]) =>
  api.put(`/products/${id}/images`, { images, primaryIndex: 0 }).then(r => r.data)
  // Sincronizar carrito de invitado con el backend
export const syncGuestCart = async (items: any[], sessionId: string) => {
  // Crear o recuperar carrito por sessionId
  const cart = await api.post('/cart/guest', { sessionId }).then(r => r.data)
  // Agregar cada item al carrito
  for (const item of items) {
    await api.post('/cart/items', {
      cartId:    cart.id,
      variantId: item.variantId,
      quantity:  item.quantity,
    }).catch(() => {}) // ignorar duplicados
  }
  return cart
}